import React, { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { useAdmin } from "./hooks/useAdmin";
import { useUnseenClaims } from "./hooks/useUnseenClaims";
import { useBadge } from "./hooks/useBadge";
import UserAccountMenu from "./UserAccountMenu";
import { isStandalone } from "./utils/installPlatform";
import "./styles.css"; // ✅ Styling here
import greekRankingLogo from "./assets/images/logos/greek-ranking-logo-circle-transparent.webp";
import primaLogo from "./assets/images/logos/prima-logo.webp";

export default function Sidebar() {
  const { isAdmin, loading, user } = useAdmin();
  const { unseenDancerClaims, unseenStudioClaims } = useUnseenClaims(isAdmin);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Close mobile menu on window resize to desktop size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMobileMenuOpen]);

  // Calculate total pending claims
  const totalPendingClaims = unseenDancerClaims + unseenStudioClaims;
  
  // 🔴 Update app icon badge with pending claims count (for admins only)
  useBadge(isAdmin ? totalPendingClaims : 0);

  return (
    <>
      {/* Mobile Header with Hamburger - Mobile Only */}
      <div className="mobile-header">
        <Link to="/" className="mobile-logo-link" onClick={closeMobileMenu}>
          <img 
            src={greekRankingLogo} 
            alt="Greek Ranking Logo" 
            className="mobile-logo"
          />
          <span className="mobile-title">Greek Ranking</span>
        </Link>
        <div className="mobile-header-actions">
          {/* Notification Badge for Admins */}
          {isAdmin && totalPendingClaims > 0 && (
            <Link to="/admin/claims" className="mobile-notification-badge" title={`${totalPendingClaims} pending claim${totalPendingClaims > 1 ? 's' : ''}`}>
              <span className="notification-count">{totalPendingClaims}</span>
            </Link>
          )}
          <button 
            className={`mobile-menu-toggle ${isMobileMenuOpen ? 'active' : ''}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>

      {/* Sidebar - Always open on desktop, toggleable on mobile */}
      <div className={`sidebar ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
        {/* Greek Ranking Logo - Desktop Only */}
        <div className="desktop-logo">
          <Link to="/" onClick={closeMobileMenu}>
            <img 
              src={greekRankingLogo} 
              alt="Greek Ranking Logo" 
              className="sidebar-logo"
            />
          </Link>
        </div>
        
        {/* User Account Menu */}
        {user && (
          <UserAccountMenu user={user} closeMobileMenu={closeMobileMenu} />
        )}
        
        <nav className="sidebar-nav">
          <NavLink to="/" onClick={closeMobileMenu} end>🏠 Homepage</NavLink>
          <NavLink to="/ranking" onClick={closeMobileMenu}>⭐ Star Ranking</NavLink>
          <NavLink to="/eternal-cup-ranking" onClick={closeMobileMenu}>🏆 Eternal Cup Ranking</NavLink>
          <NavLink to="/eternal-cup-winners" onClick={closeMobileMenu}>🏆 Eternal Cup Winners</NavLink>
          <NavLink to="/studio-rankings" onClick={closeMobileMenu}>🏛️ Studio Rankings</NavLink>
          <NavLink to="/group-ranking" onClick={closeMobileMenu}>🏆 Group Ranking</NavLink>
          <NavLink to="/compare-dancers" onClick={closeMobileMenu}>🆚 Compare Dancers</NavLink>
          <NavLink to="/points-calculator" onClick={closeMobileMenu}>🧮 Points Calculator</NavLink>

          {/* My Feed and My Dashboard live in the user account dropdown (UserAccountMenu) */}
          {user && <NavLink to="/notifications" onClick={closeMobileMenu}>🔔 Notifications</NavLink>}
          {!isStandalone() && <NavLink to="/install" onClick={closeMobileMenu}>📲 Install App</NavLink>}
          
          {/* Admin-only items */}
          {isAdmin && (
            <>
              <NavLink to="/admin" onClick={closeMobileMenu} style={{ fontWeight: 'bold' }}>🛠️ Admin Dashboard</NavLink>
              <NavLink to="/homepage-manager" onClick={closeMobileMenu}>🏠 Homepage Manager</NavLink>
              <NavLink to="/admin/claims" onClick={closeMobileMenu} className="nav-link-with-badge">
                👤 Review Claims
                {unseenDancerClaims > 0 && (
                  <span className="notification-badge">{unseenDancerClaims}</span>
                )}
              </NavLink>
              <NavLink to="/admin/studio-claims" onClick={closeMobileMenu} className="nav-link-with-badge">
                🏢 Review Studio Claims
                {unseenStudioClaims > 0 && (
                  <span className="notification-badge">{unseenStudioClaims}</span>
                )}
              </NavLink>
              <NavLink to="/admin/notifications" onClick={closeMobileMenu}>🔔 Push Notifications</NavLink>
            </>
          )}
          
          {/* Login link for guests */}
          {!user && (
            <NavLink to="/login" onClick={closeMobileMenu}>🔑 Login</NavLink>
          )}
          
          {/* Search options at the bottom */}
          <NavLink to="/search" onClick={closeMobileMenu}>🔍 Search Dancers</NavLink>
          <NavLink to="/studio-search" onClick={closeMobileMenu}>🏛️ Search Studios</NavLink>
        </nav>
        
        {/* Prima Sponsor Logo */}
        <div className="sponsor-section">
          <p className="sponsor-text">Sponsored by</p>
          <a 
            href="https://primadance.gr" 
            target="_blank" 
            rel="noopener noreferrer"
            className="sponsor-link"
          >
            <img 
              src={primaLogo} 
              alt="Prima Dance Shoes" 
              className="sponsor-logo"
            />
          </a>
        </div>
        
        {loading && (
          <div className="loading-text">
            Loading...
          </div>
        )}
      </div>
    </>
  );
}